import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { calculators, calculatorsByCategory, CATEGORIES } from '@/registry'
import { CalculatorCard } from '@/components/ui/CalculatorCard'
import { useSearch } from '@/hooks/useSearch'
import { useState } from 'react'
import { buildLocaleSeoLinks } from '@/i18n/seo'
import { resolveLocaleFromPath, toCurrentLocalePath } from '@/i18n/routing'
import { getHomePageContent } from './content'

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://toolvance.com'
const POPULAR_SLUGS = ['ohms-law', 'battery-runtime', 'electricity-cost', 'torque', 'gear-ratio', 'thermal-resistance']

export function HomePage() {
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const results = useSearch(calculators, query)
  const seoLinks = buildLocaleSeoLinks(pathname, SITE_URL)
  const { locale } = resolveLocaleFromPath(pathname)
  const content = getHomePageContent(locale)
  const localize = (path: string) => toCurrentLocalePath(pathname, path)

  const popular = POPULAR_SLUGS.map(slug => calculators.find(c => c.slug === slug)).filter(Boolean) as typeof calculators

  const resultNoun = results.length === 1 ? content.resultNounSingular : content.resultNounPlural

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Toolvance',
    url: seoLinks.canonicalUrl,
    description: content.seoDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${seoLinks.canonicalUrl}?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }

  return (
    <>
      <Helmet>
        <title>{content.seoTitle}</title>
        <meta name="description" content={content.seoDescription} />
        <link rel="canonical" href={seoLinks.canonicalUrl} />
        {seoLinks.alternates.map(alt => (
          <link key={alt.locale} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={seoLinks.xDefaultUrl} />
        <meta property="og:title" content={content.seoTitle} />
        <meta property="og:description" content={content.ogDescription} />
        <meta property="og:url" content={seoLinks.canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-brand-900 via-brand-700 to-brand-500 text-white py-14 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold tracking-tight mb-3">Toolvance</h1>
          <p className="text-lg text-blue-200 mb-8">{content.tagline}</p>
          <div className="max-w-xl mx-auto flex gap-2">
            <input
              type="search"
              placeholder={content.searchPlaceholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="flex-1 px-4 py-3 rounded-l-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-white"
              aria-label={content.searchButton}
            />
            <button
              className="px-6 py-3 bg-white text-brand-700 font-semibold rounded-r-lg hover:bg-blue-50 transition-colors"
              onClick={() => {}}
              aria-label={content.searchButton}
            >
              {content.searchButton}
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {query.trim() && (
          <section className="mb-12">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {content.searchResultsPrefix} "{query}" - {results.length} {resultNoun}
            </h2>
            {results.length === 0 ? (
              <p className="text-gray-500">{content.noResults}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {results.map(c => <CalculatorCard key={c.slug} calc={c} />)}
              </div>
            )}
          </section>
        )}

        {!query.trim() && (
          <>
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-5">{content.popularHeading}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {popular.map(c => <CalculatorCard key={c.slug} calc={c} />)}
              </div>
            </section>

            {CATEGORIES.map(cat => {
              const catCalcs = calculatorsByCategory[cat.id] || []
              return (
                <section key={cat.id} className="mb-12">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {cat.icon} {cat.label}
                    </h2>
                    <Link to={localize(`/${cat.id}`)} className="text-sm text-brand-600 hover:text-brand-700 font-medium">
                      {content.viewAllPrefix} {cat.label} {'->'}
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {catCalcs.map(c => <CalculatorCard key={c.slug} calc={c} />)}
                  </div>
                </section>
              )
            })}
          </>
        )}
      </div>
    </>
  )
}
